/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';

import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Autocomplete as MuiAutocomplete
} from '@mui/material';
import { IMark } from '@modules/mark/types';
import { useForm, Controller } from 'react-hook-form';
import { useGetMarketsQuery } from '@modules/market';
import { withYMaps } from '@pbe/react-yandex-maps';
import { useCategories } from '@app/hooks/useCategories';
import DeleteButton from 'common/DeleteButton';

interface MarkDrawerProps {
  mark: IMark | null;
  newLocation: { lat: number; lng: number } | null;
  onClose: () => void;
  onSave: (data: any) => void;
  onDelete?: (id: string) => void;
  onLocationSelect?: (lat: number, lng: number) => void;
  ymaps?: any;
}

const ARMENIA_BOUNDS = [
  [38.82, 43.45], // South-West
  [41.30, 46.63], // North-East
];

interface AddressOption {
  label: string;
  value: string;
  coords?: [number, number];
}

const MarkDrawerComponent: React.FC<MarkDrawerProps> = ({
  mark,
  newLocation,
  onClose,
  onSave,
  onDelete,
  onLocationSelect,
  ymaps,
}) => {
  const { data: marketsData } = useGetMarketsQuery({ limit: 100, offset: 0 });
  const markets = marketsData?.markets || [];
  const { categories } = useCategories();

  const [options, setOptions] = useState<AddressOption[]>([]);
  const [inputValue, setInputValue] = useState('');

  const { control, handleSubmit, reset, setValue } = useForm({
    defaultValues: {
      address: '',
      placeId: '',
      latitude: '',
      longitude: '',
      marketId: '',
      categoryIds: [] as string[],
      visible: true,
    }
  });

  useEffect(() => {
    if (mark) {
      reset({
        address: mark.address || '',
        placeId: mark.placeId || '',
        latitude: mark.latitude,
        longitude: mark.longitude,
        marketId: mark.marketId || mark.market?.id || '',
        categoryIds: mark.categories?.map((c: any) => c.id) || [],
        visible: mark.visible !== undefined ? mark.visible : true,
      });
      setInputValue(mark.address || '');
    } else if (newLocation) {
      reset({
        address: '',
        placeId: '',
        latitude: newLocation.lat.toString(),
        longitude: newLocation.lng.toString(),
        marketId: '',
        categoryIds: [],
        visible: true,
      });
      setInputValue('');

      // Try to reverse geocode new location
      if (ymaps && newLocation) {
        ymaps.geocode([newLocation.lat, newLocation.lng]).then((res: any) => {
          const firstGeoObject = res.geoObjects.get(0);

          if (firstGeoObject) {
            const address = firstGeoObject.getAddressLine();

            setValue('address', address);
            setInputValue(address);
          }
        }).catch((err: any) => console.error('Geocode error:', err));
      }
    }
  }, [mark, newLocation, reset, ymaps, setValue]);

  useEffect(() => {
    let active = true;

    if (!inputValue.trim() || !ymaps) {
      setOptions([]);

      return undefined;
    }

    const timer = setTimeout(() => {
      ymaps.geocode(inputValue, {
        boundedBy: ARMENIA_BOUNDS,
        strictBounds: true,
        results: 8,
      }).then((res: any) => {
        if (active) {
          const geoObjects = res.geoObjects.toArray();

          setOptions(geoObjects.map((obj: any) => {
            const address = obj.getAddressLine();
            const coords = obj.geometry?.getCoordinates();

            return { label: address, value: address, coords };
          }));
        }
      }).catch((err: any) => console.error('Geocode search error:', err));
    }, 350);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [inputValue, ymaps]);

  const onSubmit = (data: any) => {
    onSave(data);
  };

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">{mark ? 'Edit Mark' : 'Add Mark'}</Typography>
        {mark && onDelete && (
          <DeleteButton
            text="Delete"
            confirmationText="Do you really want to delete this mark?"
            buttonVariant="outlined"
            buttonColor="error"
            callBack={() => onDelete(mark.id)}
          />
        )}
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Search Address
        </Typography>

        <MuiAutocomplete
          freeSolo
          getOptionLabel={(option: any) => (typeof option === 'string' ? option : option?.label || '')}
          filterOptions={(x) => x}
          options={options}
          autoComplete
          includeInputInList
          filterSelectedOptions
          value={inputValue}
          noOptionsText="No locations in Armenia"
          onChange={(event: any, newValue: any) => {
            setOptions(newValue ? [newValue, ...options] : options);

            const selectedAddress = typeof newValue === 'string' ? newValue : newValue?.value || '';

            setInputValue(selectedAddress);
            setValue('address', selectedAddress);

            // Use directly resolved coordinates if available
            if (newValue && typeof newValue !== 'string' && newValue.coords) {
              const [lat, lng] = newValue.coords;

              setValue('latitude', lat.toString());
              setValue('longitude', lng.toString());

              if (onLocationSelect) {
                onLocationSelect(lat, lng);
              }

              return;
            }

            if (ymaps && selectedAddress) {
              ymaps.geocode(selectedAddress, {
                boundedBy: ARMENIA_BOUNDS,
                strictBounds: true,
                results: 1,
              }).then((res: any) => {
                const firstGeoObject = res.geoObjects.get(0);

                if (firstGeoObject) {
                  const coords = firstGeoObject.geometry.getCoordinates();

                  setValue('latitude', coords[0].toString());
                  setValue('longitude', coords[1].toString());

                  if (onLocationSelect) {
                    onLocationSelect(coords[0], coords[1]);
                  }
                }
              }).catch((err: any) => console.error('Geocode address error:', err));
            }
          }}
          onInputChange={(event, newInputValue) => {
            setInputValue(newInputValue);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search Address"
              placeholder="e.g. Komitas 15, Mayakovski, Tumanyan 10..."
              helperText="Enter physical address (street, building, village or city in Armenia)"
              fullWidth
            />
          )}
        />
      </Box>

      <Controller
        name="address"
        control={control}
        render={({ field }) => (
          <TextField {...field} label="Address" fullWidth />
        )}
      />

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Controller
          name="latitude"
          control={control}
          render={({ field }) => (
            <TextField {...field} label="Latitude" fullWidth />
          )}
        />
        <Controller
          name="longitude"
          control={control}
          render={({ field }) => (
            <TextField {...field} label="Longitude" fullWidth />
          )}
        />
      </Box>

      <Controller
        name="marketId"
        control={control}
        render={({ field }) => (
          <FormControl fullWidth>
            <InputLabel>Market</InputLabel>
            <Select {...field} label="Market" value={field.value || ''}>
              <MenuItem value="">None</MenuItem>
              {markets.map((m) => (
                <MenuItem key={m.id} value={m.id}>
                  {m.name}
                  {' '}
                  {!m.visible ? ' (Hidden on site)' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      />

      <Controller
        name="categoryIds"
        control={control}
        render={({ field }) => (
          <FormControl fullWidth>
            <InputLabel>Categories</InputLabel>
            <Select {...field} multiple label="Categories" value={field.value || []}>
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>
                  {cat.title || cat.nameEn || cat.nameAm}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      />
      <Controller
        name="visible"
        control={control}
        render={({ field }) => (
          <FormControlLabel control={<Checkbox {...field} checked={field.value} />} label="Visible" />
        )}
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="contained" color="primary" onClick={handleSubmit(onSubmit)}>
            Save
          </Button>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
        </Box>
        {mark && onDelete && (
          <DeleteButton
            text="Delete"
            confirmationText="Do you really want to delete this mark?"
            buttonVariant="outlined"
            buttonColor="error"
            callBack={() => onDelete(mark.id)}
          />
        )}
      </Box>
    </Box>
  );
};

const MarkDrawer = withYMaps(MarkDrawerComponent, false, ['geocode']);

export default MarkDrawer;
